import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/api";
import { API_ENDPOINTS } from "../constants/actionTypes";

// Select All
export const fetchAllQueueTokens = createAsyncThunk(
  "queueToken/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        API_ENDPOINTS.QUEUE_TOKEN.SELECT_ALL,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch all tokens",
      );
    }
  },
);

// Select By Id
export const fetchQueueTokenById = createAsyncThunk(
  "queueToken/fetchById",
  async (tokenId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.QUEUE_TOKEN.SELECT}/${tokenId}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch token details",
      );
    }
  },
);

// Call
export const callQueueToken = createAsyncThunk(
  "queueToken/call",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${API_ENDPOINTS.QUEUE_TOKEN.CALL}?id=${payload.tokenId}`,
        {},
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to call token",
      );
    }
  },
);

// Complete
export const completeQueueToken = createAsyncThunk(
  "queueToken/complete",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${API_ENDPOINTS.QUEUE_TOKEN.COMPLETE}?id=${payload.tokenId}`,
        {},
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to complete token",
      );
    }
  },
);

// Skip
export const skipQueueToken = createAsyncThunk(
  "queueToken/skip",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${API_ENDPOINTS.QUEUE_TOKEN.SKIP}?id=${payload.tokenId}`,
        {},
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to skip token",
      );
    }
  },
);

// Live
export const fetchLiveTokens = createAsyncThunk(
  "queueToken/fetchLive",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.QUEUE_TOKEN.LIVE);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch live tokens",
      );
    }
  },
);

// Track
export const trackQueueToken = createAsyncThunk(
  "queueToken/track",
  async (tokenId, { rejectWithValue }) => {
    try {
      // Assuming track accepts tokenId as a query parameter or similar.
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.QUEUE_TOKEN.TRACK}?tokenId=${tokenId}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to track token",
      );
    }
  },
);

const queueTokenSlice = createSlice({
  name: "queueToken",
  initialState: {
    tokensList: [],
    liveTokens: [],
    selectedToken: null,
    trackInfo: null,
    isLoading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    clearQueueTokenState: (state) => {
      state.isLoading = false;
      state.error = null;
      state.successMessage = null;
    },
    clearSelectedToken: (state) => {
      state.selectedToken = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllQueueTokens.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllQueueTokens.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.tokensList = apiData?.data || apiData || action.payload || [];
      })
      .addCase(fetchAllQueueTokens.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch By Id
      .addCase(fetchQueueTokenById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQueueTokenById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedToken = action.payload?.data || action.payload;
      })
      .addCase(fetchQueueTokenById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Call
      .addCase(callQueueToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(callQueueToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successMessage =
          action.payload?.message || "Token called successfully!";
      })
      .addCase(callQueueToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Complete
      .addCase(completeQueueToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(completeQueueToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successMessage =
          action.payload?.message || "Token completed successfully!";
      })
      .addCase(completeQueueToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Skip
      .addCase(skipQueueToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(skipQueueToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.successMessage =
          action.payload?.message || "Token skipped successfully!";
      })
      .addCase(skipQueueToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Live
      .addCase(fetchLiveTokens.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLiveTokens.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.liveTokens = apiData?.data || apiData || action.payload || [];
      })
      .addCase(fetchLiveTokens.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Track
      .addCase(trackQueueToken.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(trackQueueToken.fulfilled, (state, action) => {
        state.isLoading = false;
        state.trackInfo = action.payload?.data || action.payload;
      })
      .addCase(trackQueueToken.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearQueueTokenState, clearSelectedToken } =
  queueTokenSlice.actions;
export default queueTokenSlice.reducer;
